/**
 * =============================================================================
 * LEGACY ADMIN LAYOUT — the flag-OFF fallback surface (ADR-025 §3.5, B-1)
 * =============================================================================
 *
 * The pre-B-1 single-scroll admin, extracted VERBATIM from `main.tsx`'s
 * `AdminContent` (minus the post-install/tokenless branches, which moved up
 * into `AdminApp` and apply to both surfaces). This is the "prior surface one
 * revert away" the §3.5 release-toggle contract requires while
 * `adminWorkbenchShell` bakes: flag OFF (the default) renders EXACTLY the
 * layout, ordering, and startup behaviour production has today — including
 * the default-open analytics block (001A's reorder was folded INTO the shell
 * per §13.2.1; this fallback is deliberately untouched rollback surface, not
 * a second place to improve).
 *
 * DELETE WHOLESALE at flag retirement (P1 pilot pass + recorded OFF→ON→OFF
 * drill + owner sign-off — see the flag registry in `@find-my-part/types`).
 * =============================================================================
 */

import React, { useState } from 'react';
import { Box, Button, Flex, FlexItem } from '@bigcommerce/big-design';
import { ExpandMoreIcon, ExpandLessIcon } from '@bigcommerce/big-design-icons';
import { useSession } from '../../hooks/useSession';
import { QuestionQueue } from '../Questions/QuestionQueue';
import { AnalyticsDashboard } from '../Analytics';
import { LaunchModeCard, MediaSettingsCard } from '../Settings';
import { ParticipantLookup, ConsentInspection } from '../Support';
import { AuditPanel } from '../Audit';
import { MediaReviewPanel } from '../Media';
import { TokenExpirationWarning } from '../SessionWarning';

export function LegacyAdminLayout(): React.ReactElement {
  const [showAnalytics, setShowAnalytics] = useState(true);
  const { isExpiringSoon, minutesUntilExpiry, dismissExpiryWarning } = useSession();

  return (
    <>
      {/* Token Expiration Warning Banner */}
      {isExpiringSoon && (
        <Box padding="medium" paddingBottom="none">
          <TokenExpirationWarning
            minutesRemaining={minutesUntilExpiry}
            onDismiss={dismissExpiryWarning}
          />
        </Box>
      )}

      {/* Public availability control (Phase 4.5 controlled-launch slice) */}
      <Box padding="medium" paddingBottom="none">
        <LaunchModeCard />
      </Box>

      {/* §10.1 tenant media settings (ADR-022 — PR4b, BL-IMG-ADMIN-AUTHORING-001) */}
      <Box padding="medium" paddingBottom="none">
        <MediaSettingsCard />
      </Box>

      {/* Read-only participant support lookup (BL-ADMIN-PARTICIPANT-SUPPORT-LOOKUP-001) */}
      <Box padding="medium" paddingBottom="none">
        <ParticipantLookup />
      </Box>

      {/* Read-only consent-record inspection (BL-PRIVACY-CONSENT-CAPTURE-001) */}
      <Box padding="medium" paddingBottom="none">
        <ConsentInspection />
      </Box>

      {/* Read-only admin audit-trail viewer (PR I-2 — ADR-023 / BL-ADMIN-AUDIT-VIEWER-001) */}
      <Box padding="medium" paddingBottom="none">
        <AuditPanel />
      </Box>

      {/* Analytics Toggle */}
      <Box padding="medium" paddingBottom="none">
        <Flex justifyContent="flex-end">
          <FlexItem>
            <Button
              variant="subtle"
              onClick={() => setShowAnalytics((prev) => !prev)}
              iconRight={showAnalytics ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              data-testid="analytics-toggle"
            >
              {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
            </Button>
          </FlexItem>
        </Flex>
      </Box>

      {/* Analytics Dashboard (collapsible) */}
      {showAnalytics && (
        <Box marginBottom="medium">
          <AnalyticsDashboard />
        </Box>
      )}

      {/* Media moderation review queue (ADR-022 §10 — BL-IMG-ADMIN-MODERATION-001) */}
      <Box padding="medium" paddingBottom="none">
        <MediaReviewPanel />
      </Box>

      {/* Question Queue */}
      <QuestionQueue data-testid="question-queue" />
    </>
  );
}
